import { RAI_SSO_SERVICE_URL } from "../definitions";
import { RaissoError, errorFactory, ServiceResponse } from "../responses";
import { R } from "@mobily/ts-belt";
import { GenericResponse, ParentalControlLevelRequest, ParentalControlResetPinRequest, ParentalControlUpdatePinRequest } from "./service-models"
import { raisso } from "../raisso";
import { responseValidation } from "../utils"
import { genericResponseSchema } from "./utils"

const BASE_URL = `${RAI_SSO_SERVICE_URL}/parental-control`

export { BASE_URL as PARENTAL_CONTROL_BASE_URL }

function traceError(e: unknown) {
  console.error('[parental-control-controller] Error:', e);
}

class ParentalControlController {
  private constructor() {}

  private static _instance: ParentalControlController;
  public static get instance(): ParentalControlController {
    if (!this._instance) {
      this._instance = new ParentalControlController();
    }
    return this._instance;
  }

  public async updatePin(data: ParentalControlUpdatePinRequest): ServiceResponse<GenericResponse> {
    const url = `${BASE_URL}/pin`;
    try {
      const response = await raisso.callAuthFetch<GenericResponse>(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      const value = R.map(response, responseValidation(genericResponseSchema));
      return value;
    } catch (e) {
      traceError(e);
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e, context: { request: { url, body: JSON.stringify(data) } } }
      ));
    }
  }

  public async savePin(newPin: string): ServiceResponse<GenericResponse> {
    const url = `${BASE_URL}/pin`;
    try {
      const response = await raisso.callAuthFetch<GenericResponse>(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ newPin }),
      });
      const value = R.map(response, responseValidation(genericResponseSchema));
      return value;
    } catch (e) {
      traceError(e);
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e, context: { request: { url, body: JSON.stringify({ newPin }) } }}
      ));
    }
  }

  public async updateLevel(data: ParentalControlLevelRequest): ServiceResponse<GenericResponse> {
    const url = `${BASE_URL}/level`;
    try {
      const response = await raisso.callAuthFetch<GenericResponse>(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const value = R.map(response, responseValidation(genericResponseSchema));
      return value;
    } catch (e) {
      traceError(e);
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e, context: { request: { url, body: JSON.stringify(data) } } }
      ));
    }
  }

  public async resetPin(data: ParentalControlResetPinRequest, tokenAuth: string, tokenReset: string): ServiceResponse<GenericResponse> {
    const url = `${BASE_URL}/reset/pin`;
    try {
      const response = await raisso.callAuthFetch<GenericResponse>(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Auth-Token' : tokenAuth, 'Reset-Pin-Token': tokenReset },
        body: JSON.stringify(data),
      });
      const value = R.map(response, responseValidation(genericResponseSchema));
      return value;
    } catch (e) {
      traceError(e);
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e, context: { request: { url, body: JSON.stringify(data) } } }
      ));
    }
  }

  public async requestPinReset(): ServiceResponse<GenericResponse> {
    const url = `${BASE_URL}/request/pin-reset`;
    try {
      const response = await raisso.callAuthFetch<GenericResponse>(url, {
        method: 'POST'
      });
      const value = R.map(response, responseValidation(genericResponseSchema));
      return value;
    } catch (e) {
      traceError(e);
      return R.Error(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e, context: { request: { url } } }
      ));
    }
  }

  public async delete(currentPin: string): ServiceResponse<GenericResponse> {
    const url = `${BASE_URL}/`;
    try {
      const response = await raisso.callAuthFetch<GenericResponse>(url, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPin }),
      });
      const value = R.map(response, responseValidation(genericResponseSchema));
      return value;
    } catch (e) {
      traceError(e);
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e, context: { request: { url, body: JSON.stringify({ currentPin }) } } }
      ));
    }
  }

  public async checkTokenResetPin(tokenAuth: string, tokenReset: string): ServiceResponse<GenericResponse> {
    const url = `${BASE_URL}/request/pin-reset/check-token`;
    try {
      const response = await raisso.callAuthFetch<GenericResponse>(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'X-Auth-Token' : tokenAuth, 'Reset-Pin-Token': tokenReset }
      });
      const value = R.map(response, responseValidation(genericResponseSchema));
      return value;
    } catch (e) {
      traceError(e)
      return R.Error<RaissoError>(errorFactory(
        RaissoError,
        (e instanceof Error) ? e.message : 'Opps! Qualcosa è andato storto',
        { cause: e, }
      ))
    }
  }
}

export default ParentalControlController;
